import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { NpsSurvey } from '@/shared/types/nps';
import { cn } from '@/shared/lib/cn';
import { parseSurveyDescription } from './parse-description';
import { PublicSurveyTopBar } from './public-survey-top-bar';

interface PublicSurveyLayoutProps {
  survey: NpsSurvey;
  children: ReactNode;
  className?: string;
}

export function PublicSurveyLayout({ survey, children, className }: PublicSurveyLayoutProps) {
  const { t } = useTranslation();
  const user = survey.user;

  return (
    <div className={cn('flex flex-1 flex-col bg-background', className)}>
      <PublicSurveyTopBar />

      {user?.banner ? (
        <header className="flex w-full justify-center py-4 sm:py-6 md:py-8">
          <div className="relative h-24 w-[92%] max-w-3xl overflow-hidden rounded-xl sm:h-36 md:h-48">
            <img
              src={user.banner}
              alt=""
              className="size-full rounded-xl object-cover object-center"
            />
          </div>
        </header>
      ) : null}

      <main className="container mx-auto max-w-3xl flex-1 px-3 pb-6 sm:px-4 sm:pb-10">
        <div className="mb-5 text-center sm:mb-8">
          <h1 className="mb-1.5 text-xl font-bold tracking-tight sm:mb-2 sm:text-2xl md:text-3xl">
            {survey.title}
          </h1>
          {survey.description ? (
            <p className="text-sm text-muted-foreground sm:text-base">
              {parseSurveyDescription(survey.description)}
            </p>
          ) : null}
        </div>

        {children}
      </main>

      <footer className="hidden border-t border-border/60 bg-muted/30 py-6 sm:block md:py-8">
        <div className="container mx-auto flex max-w-3xl flex-col items-center gap-6 px-4 md:flex-row md:items-start">
          {user?.profile ? (
            <div className="relative size-28 shrink-0 overflow-hidden rounded-xl sm:size-32">
              <img src={user.profile} alt="" className="size-full object-cover" />
            </div>
          ) : null}
          <div className="text-center md:text-left">
            <h3 className="mb-2 text-lg font-medium">{t('layout.thanksTitle')}</h3>
            <p className="text-sm text-muted-foreground">{t('layout.thanksDescription')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
